/**
 * Sudoku — Pencil-on-Notebook-Paper renderer.
 *
 * The game IS a real notebook page. The grid is hand-ruled, the numbers are
 * pencil-on-paper (slight rotation, slight weight variation), candidate marks
 * tuck into the cell corners in tiny pencil. Erasing leaves a faint smudge
 * that lingers ~600ms.
 *
 * Selection: a sketched rectangle draws around the selected cell via SVG
 * stroke-dasharray over ~280ms.
 *
 * Input model:
 *   - Click/tap a cell to select.
 *   - Click a digit on the bottom panel to fill (or toggle candidate in pencil
 *     mode).
 *   - Press P to toggle pencil mode (also a clearly-labeled button).
 *   - Backspace / eraser button erases.
 *   - Undo button reverses last move.
 *   - Hint draws a number in teacher's-red-pencil into a random empty/wrong
 *     cell.
 */
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmbedded } from '@/host/isEmbedded';
import { triggerHaptic } from '@/juice';
import { usePreferences } from '@/preferences';
import {
  difficultyLabel,
  formatElapsed,
  useSudokuEngine,
  type SudokuDifficulty,
  type SudokuDigit,
  type SudokuMode,
} from '@/games/sudoku/engine';
import type { DifficultyEngine, GameSession } from '@rendever/games-sdk';

import { Affirmation } from './Affirmation';
import { Celebration } from './Celebration';
import { SMUDGE_LINGER_MS, placeSpring } from './motion';
import { sound } from './sound';

import './Sudoku.css';

export interface NotebookSudokuProps {
  session: GameSession;
  difficulty: DifficultyEngine;
  initialDifficulty?: SudokuDifficulty;
}

const DIGITS: ReadonlyArray<SudokuDigit> = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export function Sudoku({
  session,
  difficulty,
  initialDifficulty,
}: NotebookSudokuProps): React.JSX.Element {
  const reduced = useReducedMotion();
  const { theme } = usePreferences();

  const initialFromUrl: SudokuDifficulty = useMemo(() => {
    if (initialDifficulty) return initialDifficulty;
    if (typeof window === 'undefined') return 'easy';
    const p = new URLSearchParams(window.location.search);
    const d = p.get('difficulty');
    if (d === 'easy' || d === 'medium' || d === 'hard' || d === 'expert') return d;
    return 'easy';
  }, [initialDifficulty]);

  const modeFromUrl: SudokuMode = useMemo(() => {
    if (typeof window === 'undefined') return 'daily';
    const p = new URLSearchParams(window.location.search);
    return p.get('mode') === 'practice' ? 'practice' : 'daily';
  }, []);
  const [playMode, setPlayMode] = useState<SudokuMode>(modeFromUrl);

  const { state, actions } = useSudokuEngine({
    session,
    difficulty,
    initialDifficulty: initialFromUrl,
    mode: playMode,
  });

  // Start on first paint — no separate "begin" screen needed; the page IS the puzzle.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    sound.unlock();
    sound.play('pageRustle');
    actions.start(initialFromUrl);
    // Dev / screenshot helper: ?demo=solved triggers an instant solve.
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search);
      if (p.get('demo') === 'solved') {
        window.setTimeout(() => actions.solveNow(), 400);
      }
    }
  }, [actions, initialFromUrl]);

  // Keyboard input.
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (state.phase !== 'play') return;
      if (e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        actions.togglePencilMode();
        sound.play('pencilTap');
        triggerHaptic('light');
        return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete' || e.key === '0') {
        e.preventDefault();
        if (state.selected !== null) {
          actions.erase();
          sound.play('eraserRub');
          triggerHaptic('light');
        }
        return;
      }
      if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        actions.undo();
        sound.play('pencilTap');
        return;
      }
      if (/^[1-9]$/.test(e.key)) {
        e.preventDefault();
        if (state.selected !== null) {
          const d = Number(e.key) as SudokuDigit;
          actions.placeDigit(d);
          sound.play(state.pencilMode ? 'candidateScribble' : 'pencilScratchLong');
          triggerHaptic(state.pencilMode ? 'light' : 'medium');
        }
        return;
      }
      // Arrow navigation.
      if (state.selected !== null) {
        const row = Math.floor(state.selected / 9);
        const col = state.selected % 9;
        let nr = row;
        let nc = col;
        if (e.key === 'ArrowUp') nr = Math.max(0, row - 1);
        else if (e.key === 'ArrowDown') nr = Math.min(8, row + 1);
        else if (e.key === 'ArrowLeft') nc = Math.max(0, col - 1);
        else if (e.key === 'ArrowRight') nc = Math.min(8, col + 1);
        else return;
        e.preventDefault();
        const next = nr * 9 + nc;
        if (next !== state.selected) {
          actions.selectCell(next);
          sound.play('select');
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [state.phase, state.selected, state.pencilMode, actions]);

  const handleCellClick = useCallback(
    (i: number): void => {
      sound.unlock();
      sound.play('select');
      triggerHaptic('light');
      actions.selectCell(i === state.selected ? null : i);
    },
    [actions, state.selected],
  );

  const handleDigit = useCallback(
    (d: SudokuDigit): void => {
      if (state.selected === null) {
        sound.play('pencilTap');
        return;
      }
      actions.placeDigit(d);
      sound.play(state.pencilMode ? 'candidateScribble' : 'pencilScratchLong');
      triggerHaptic(state.pencilMode ? 'light' : 'medium');
    },
    [actions, state.selected, state.pencilMode],
  );

  const handleErase = useCallback((): void => {
    if (state.selected === null) return;
    actions.erase();
    sound.play('eraserRub');
    triggerHaptic('light');
  }, [actions, state.selected]);

  const handleUndo = useCallback((): void => {
    actions.undo();
    sound.play('pencilTap');
    triggerHaptic('light');
  }, [actions]);

  const handlePencilToggle = useCallback((): void => {
    actions.togglePencilMode();
    sound.play('pencilTap');
    triggerHaptic('light');
  }, [actions]);

  const handleHint = useCallback((): void => {
    actions.hint();
    sound.play('pencilScratchLong');
    triggerHaptic('light');
  }, [actions]);

  const handleNextPuzzle = useCallback((): void => {
    sound.play('pageRustle');
    triggerHaptic('light');
    if (playMode === 'daily') {
      setPlayMode('practice');
      actions.nextPuzzle('practice');
      return;
    }
    actions.nextPuzzle();
  }, [actions, playMode]);

  /* Note this one goes to `/` — the games wall — where every sibling game
     returns to its own sub-hub. That looks like a bug even standalone, but
     standalone behaviour is deliberately left byte-identical here; what changes
     is that embedded it does not navigate at all. The host chose this title and
     owns the way off it, and walking a resident onto the full catalog is the
     exact gate bypass this pass exists to close. */
  const handleBackToHub = useCallback((): void => {
    sound.play('pencilTap');
    void actions.end();
    if (isEmbedded()) return;
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  }, [actions]);

  // Pre-compute peer + same-digit highlights.
  const selectedDigit = state.selected !== null ? (state.board[state.selected]?.value ?? 0) : 0;
  const selectedRow = state.selected !== null ? Math.floor(state.selected / 9) : -1;
  const selectedCol = state.selected !== null ? state.selected % 9 : -1;
  const selectedBox =
    state.selected !== null ? Math.floor(selectedRow / 3) * 3 + Math.floor(selectedCol / 3) : -1;

  return (
    <div className="nb-sudoku" data-theme={theme} data-phase={state.phase}>
      <div className="nb-sudoku__desk" aria-hidden="true" />
      <div className="nb-sudoku__page">
        <PaperBackdrop />

        <header className="nb-sudoku__head">
          <div className="nb-sudoku__head-left">
            {/* Embedded, the host's Back is already on screen and this one
                would lead to the games catalog instead. */}
            {!isEmbedded() && (
              <button
                type="button"
                className="nb-sudoku__back"
                onClick={handleBackToHub}
                aria-label="Back to puzzles"
              >
                <span aria-hidden="true">←</span>
                <span>Back</span>
              </button>
            )}
            <p className="nb-sudoku__subject">
              <span className="nb-sudoku__subject-val">
                {playMode === 'daily' ? 'Daily Sudoku' : 'Practice Sudoku'} ·{' '}
                {difficultyLabel(state.difficulty)}
              </span>
            </p>
          </div>

          <div className="nb-sudoku__head-right">
            <Stat label="time" value={formatElapsed(state.elapsedSec)} />
            <Stat
              label="filled"
              value={`${state.correctCount}/${81 - state.puzzle.givens.filter((v) => v === 0).length + state.correctCount}`}
            />
            <Stat label="hints" value={String(state.hintsUsed)} muted={state.hintsUsed === 0} />
          </div>
        </header>

        <main className="nb-sudoku__main">
          <Board
            state={state}
            selectedRow={selectedRow}
            selectedCol={selectedCol}
            selectedBox={selectedBox}
            selectedDigit={selectedDigit}
            onCellClick={handleCellClick}
            reduced={!!reduced}
          />

          <div className="nb-sudoku__side">
            <DigitPad
              board={state.board}
              pencilMode={state.pencilMode}
              disabled={state.phase !== 'play'}
              selectedDigit={selectedDigit}
              onDigit={handleDigit}
            />

            <div className="nb-sudoku__controls">
              <ControlButton
                label="Pencil"
                hint="P"
                active={state.pencilMode}
                onClick={handlePencilToggle}
                icon={<PencilIcon />}
              />
              <ControlButton
                label="Erase"
                hint="⌫"
                onClick={handleErase}
                disabled={state.selected === null}
                icon={<EraserIcon />}
              />
              <ControlButton
                label="Undo"
                hint="⌘Z"
                onClick={handleUndo}
                disabled={!state.undoAvailable}
                icon={<UndoIcon />}
              />
              <ControlButton
                label="Hint"
                hint=""
                onClick={handleHint}
                icon={<HintIcon />}
                variant="teacher"
              />
            </div>
          </div>
        </main>
      </div>

      <AnimatePresence>
        {state.phase === 'solved' && (
          <motion.div
            key="celebrate"
            className="nb-sudoku__overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Celebration active={state.phase === 'solved'} elapsedSec={state.elapsedSec} />
            <Affirmation
              difficulty={state.difficulty}
              mode={playMode}
              elapsedSec={state.elapsedSec}
              hintsUsed={state.hintsUsed}
              mistakeCount={state.mistakeCount}
              onAnother={handleNextPuzzle}
              onBack={handleBackToHub}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {state.pencilMode && (
        <div className="nb-sudoku__pencil-banner" role="status">
          <PencilIcon />
          <span>Pencil mode — tap a number to leave a candidate mark.</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat — small label/value pair in the header
// ---------------------------------------------------------------------------

function Stat({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="nb-stat" data-muted={muted ? 'true' : 'false'}>
      <span className="nb-stat__label">{label}</span>
      <span className="nb-stat__value">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Board
// ---------------------------------------------------------------------------

function Board({
  state,
  selectedRow,
  selectedCol,
  selectedBox,
  selectedDigit,
  onCellClick,
  reduced,
}: {
  state: ReturnType<typeof useSudokuEngine>['state'];
  selectedRow: number;
  selectedCol: number;
  selectedBox: number;
  selectedDigit: number;
  onCellClick: (i: number) => void;
  reduced: boolean;
}) {
  return (
    <div className="nb-board" data-pencil={state.pencilMode ? 'true' : 'false'}>
      <BoardGridLines />

      <div className="nb-board__cells">
        {state.board.map((cell, i) => {
          const row = Math.floor(i / 9);
          const col = i % 9;
          const box = Math.floor(row / 3) * 3 + Math.floor(col / 3);
          const isSelected = i === state.selected;
          const isPeer =
            state.selected !== null &&
            i !== state.selected &&
            (row === selectedRow || col === selectedCol || box === selectedBox);
          const isSameDigit =
            selectedDigit !== 0 && cell.value === selectedDigit && cell.value !== 0;

          return (
            <Cell
              key={i}
              cell={cell}
              row={row}
              col={col}
              isSelected={isSelected}
              isPeer={isPeer}
              isSameDigit={isSameDigit}
              onClick={() => onCellClick(i)}
              reduced={reduced}
            />
          );
        })}
      </div>
    </div>
  );
}

function BoardGridLines() {
  // Hand-drawn-ish grid lines. The outer + box borders are heavier; the cell
  // borders thinner and wobbly via SVG path.
  const lines: React.ReactElement[] = [];
  const SZ = 540;
  const step = SZ / 9;

  // Inner thin lines first.
  for (let i = 1; i < 9; i += 1) {
    if (i % 3 === 0) continue;
    lines.push(
      <line
        key={`h-${i}`}
        x1={0}
        y1={i * step}
        x2={SZ}
        y2={i * step}
        className="nb-board__line nb-board__line--thin"
      />,
    );
    lines.push(
      <line
        key={`v-${i}`}
        x1={i * step}
        y1={0}
        x2={i * step}
        y2={SZ}
        className="nb-board__line nb-board__line--thin"
      />,
    );
  }

  // Box-thick lines — hand-drawn paths with slight wobble.
  for (let i = 0; i <= 3; i += 1) {
    const y = i * step * 3;
    const x = i * step * 3;
    lines.push(
      <path
        key={`h-bold-${i}`}
        d={wobblePath(0, y, SZ, y, 8, i * 7)}
        className="nb-board__line nb-board__line--bold"
      />,
    );
    lines.push(
      <path
        key={`v-bold-${i}`}
        d={wobblePath(x, 0, x, SZ, 8, i * 11 + 3)}
        className="nb-board__line nb-board__line--bold"
      />,
    );
  }

  return (
    <svg
      className="nb-board__grid"
      viewBox={`0 0 ${SZ} ${SZ}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {lines}
    </svg>
  );
}

/** Generate a wobbly path from (x1,y1) to (x2,y2) via N segments. */
function wobblePath(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  segments: number,
  seed: number,
): string {
  const horizontal = y1 === y2;
  let d = `M${x1} ${y1}`;
  for (let s = 1; s <= segments; s += 1) {
    const t = s / segments;
    const x = x1 + (x2 - x1) * t;
    const y = y1 + (y2 - y1) * t;
    const wobble = Math.sin(seed + s * 1.3) * 0.6;
    const ox = horizontal ? 0 : wobble;
    const oy = horizontal ? wobble : 0;
    d += ` L${x + ox} ${y + oy}`;
  }
  return d;
}

// ---------------------------------------------------------------------------
// Cell
// ---------------------------------------------------------------------------

function Cell({
  cell,
  row,
  col,
  isSelected,
  isPeer,
  isSameDigit,
  onClick,
  reduced,
}: {
  cell: ReturnType<typeof useSudokuEngine>['state']['board'][number];
  row: number;
  col: number;
  isSelected: boolean;
  isPeer: boolean;
  isSameDigit: boolean;
  onClick: () => void;
  reduced: boolean;
}) {
  // Eraser smudge: we render the smudge layer while it's "fresh," then fade
  // it via CSS transition.
  const [smudgeKey, setSmudgeKey] = useState(0);
  const [showSmudge, setShowSmudge] = useState(false);
  const prevValue = useRef(cell.value);

  useEffect(() => {
    if (prevValue.current !== 0 && cell.value === 0) {
      setSmudgeKey((k) => k + 1);
      setShowSmudge(true);
      const t = window.setTimeout(() => setShowSmudge(false), SMUDGE_LINGER_MS);
      prevValue.current = cell.value;
      return () => window.clearTimeout(t);
    }
    prevValue.current = cell.value;
    return undefined;
  }, [cell.value]);

  return (
    <button
      type="button"
      data-mobile-audit-ignore="dense-board"
      className="nb-cell"
      data-selected={isSelected ? 'true' : 'false'}
      data-peer={isPeer ? 'true' : 'false'}
      data-same={isSameDigit ? 'true' : 'false'}
      data-given={cell.given ? 'true' : 'false'}
      data-conflict={cell.conflict ? 'true' : 'false'}
      data-row={row}
      data-col={col}
      onClick={onClick}
      aria-label={`Row ${row + 1}, column ${col + 1}${cell.value ? `, value ${cell.value}` : ', empty'}`}
    >
      {isSelected && <SelectionRect />}

      {showSmudge && (
        <span key={smudgeKey} className="nb-cell__smudge" aria-hidden="true">
          <SmudgeBlot />
        </span>
      )}

      {cell.value !== 0 && (
        <motion.span
          key={`v-${cell.value}-${cell.tiltDeg}`}
          className="nb-cell__digit"
          data-digit={cell.value}
          initial={
            cell.given || reduced
              ? { opacity: 1, scale: 1, rotate: cell.tiltDeg }
              : { opacity: 0, scale: 0.55, rotate: cell.tiltDeg - 6 }
          }
          animate={{ opacity: 1, scale: 1, rotate: cell.tiltDeg }}
          transition={cell.given || reduced ? { duration: 0 } : placeSpring}
        >
          {cell.value}
        </motion.span>
      )}

      {cell.value === 0 && cell.candidates.size > 0 && (
        <span className="nb-cell__candidates" aria-hidden="true">
          {DIGITS.map((d) => (
            <span
              key={d}
              className="nb-cell__candidate"
              data-on={cell.candidates.has(d) ? 'true' : 'false'}
            >
              {cell.candidates.has(d) ? d : ''}
            </span>
          ))}
        </span>
      )}
    </button>
  );
}

function SelectionRect() {
  // SVG rectangle drawn via stroke-dasharray for the hand-drawn-feel select halo.
  return (
    <svg className="nb-cell__sel" viewBox="0 0 60 60" preserveAspectRatio="none" aria-hidden="true">
      <rect x="3" y="3" width="54" height="54" rx="2" className="nb-cell__sel-stroke" />
    </svg>
  );
}

function SmudgeBlot() {
  return (
    <svg viewBox="0 0 60 60" preserveAspectRatio="none" aria-hidden="true">
      <ellipse cx="30" cy="32" rx="18" ry="14" className="nb-smudge__blot" />
      <ellipse cx="34" cy="28" rx="10" ry="8" className="nb-smudge__blot nb-smudge__blot--soft" />
      <path d="M16,38 Q24,28 38,40 Q48,44 44,30" className="nb-smudge__streak" fill="none" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Digit pad
// ---------------------------------------------------------------------------

function DigitPad({
  board,
  pencilMode,
  disabled,
  selectedDigit,
  onDigit,
}: {
  board: ReturnType<typeof useSudokuEngine>['state']['board'];
  pencilMode: boolean;
  disabled: boolean;
  selectedDigit: number;
  onDigit: (d: SudokuDigit) => void;
}) {
  // Count how many of each digit remain to place.
  const counts: Record<SudokuDigit, number> = {
    1: 9,
    2: 9,
    3: 9,
    4: 9,
    5: 9,
    6: 9,
    7: 9,
    8: 9,
    9: 9,
  };
  for (const cell of board) {
    if (cell.value !== 0) counts[cell.value as SudokuDigit] -= 1;
  }

  return (
    <div
      className="nb-pad"
      data-pencil={pencilMode ? 'true' : 'false'}
      role="group"
      aria-label="Number pad"
    >
      {DIGITS.map((d) => {
        const remaining = counts[d];
        const done = remaining <= 0;
        return (
          <button
            key={d}
            type="button"
            className="nb-pad__btn"
            disabled={disabled || done}
            data-done={done ? 'true' : 'false'}
            data-active={d === selectedDigit ? 'true' : 'false'}
            onClick={() => onDigit(d)}
            aria-label={`${pencilMode ? 'Pencil-mark' : 'Place'} ${d}, ${remaining} remaining`}
          >
            <span className="nb-pad__digit" data-digit={d}>
              {d}
            </span>
            <span className="nb-pad__remaining" aria-hidden="true">
              {done ? '✓' : remaining}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Control buttons
// ---------------------------------------------------------------------------

function ControlButton({
  label,
  hint,
  icon,
  onClick,
  disabled,
  active,
  variant,
}: {
  label: string;
  hint?: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  variant?: 'teacher';
}) {
  return (
    <button
      type="button"
      className="nb-ctrl"
      onClick={onClick}
      disabled={disabled}
      data-active={active ? 'true' : 'false'}
      data-variant={variant ?? 'default'}
      aria-pressed={active ? 'true' : undefined}
    >
      <span className="nb-ctrl__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="nb-ctrl__label">{label}</span>
      {hint ? (
        <span className="nb-ctrl__hint" aria-hidden="true">
          {hint}
        </span>
      ) : null}
    </button>
  );
}

function PencilIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 21l3.5-1L20 6.5l-2.5-2.5L4 17.5z" />
      <path d="M14 6l4 4" />
      <path d="M3 21l3.5-1" opacity="0.6" />
    </svg>
  );
}

function EraserIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 4l4 4L9 19H5v-4z" />
      <path d="M8 11l4 4" opacity="0.5" />
      <path d="M3 21h14" />
    </svg>
  );
}

function UndoIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 14L4 9l5-5" />
      <path d="M4 9h11a5 5 0 0 1 0 10H9" />
    </svg>
  );
}

function HintIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3a6 6 0 0 0-4 10.5V16h8v-2.5A6 6 0 0 0 12 3z" />
      <path d="M10 19h4" />
      <path d="M11 22h2" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Paper backdrop — reused from Hub but tuned for the game page
// ---------------------------------------------------------------------------

function PaperBackdrop() {
  return (
    <>
      <div className="nb-sudoku__paper" aria-hidden="true">
        <svg width="100%" height="100%" aria-hidden="true">
          <defs>
            <filter id="nb-grain-g" x="0" y="0" width="100%" height="100%">
              <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" seed="7" />
              <feColorMatrix values="0 0 0 0 0.15  0 0 0 0 0.12  0 0 0 0 0.08  0 0 0 0.08 0" />
            </filter>
          </defs>
          <rect width="100%" height="100%" filter="url(#nb-grain-g)" />
        </svg>
      </div>

      <div className="nb-sudoku__rules" aria-hidden="true">
        {Array.from({ length: 22 }).map((_, i) => {
          const wobble = Math.sin(i * 2.3) * 0.2;
          return (
            <div
              key={i}
              className="nb-sudoku__rule"
              style={{ transform: `translateY(${wobble}px)` }}
            />
          );
        })}
      </div>

      <div className="nb-sudoku__margin" aria-hidden="true" />

      <ol className="nb-sudoku__holes" aria-hidden="true">
        {[0.15, 0.45, 0.78].map((y, i) => (
          <li key={i} className="nb-sudoku__hole" style={{ top: `${y * 100}%` }} />
        ))}
      </ol>
    </>
  );
}
